import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import { generateSavedViewUrl } from "components/saved_searches/utils";
import Button from "components/ui/button/Button";
import Card from "components/ui/card/Card";
import EllipsisTooltip from "components/ui/tooltip/EllipsisTooltip";
import { useDeletePinnedView } from "lib/hooks/api/pinned_views/useDeletePinnedView";
import { SavedViewDataTypeUnion } from "lib/types/savedViews";

type EmptyStatePinnedViewProps = {
  setSelectedView: (viewId: string, viewName: string, context: string) => void;
  dataType: SavedViewDataTypeUnion;
  id: string;
  name: string;
  description?: string;
};

const EmptyStatePinnedView = ({
  setSelectedView,
  dataType,
  id,
  name,
  description,
}: EmptyStatePinnedViewProps) => {
  const { mutate, isLoading } = useDeletePinnedView();

  return (
    <PinnedViewCard
      width={237}
      href={generateSavedViewUrl(id, dataType)}
      onClick={(e) => {
        e.preventDefault();
        setSelectedView(id, name, "Pinned view from empty state");
      }}
    >
      <Flex column gap={4}>
        <Flex alignItems="center" gap={4} height={32}>
          <EllipsisTooltip fullText={name}>
            {(ref) => (
              <PinnedViewTitle ref={ref} bold ellipsis>
                {name}
              </PinnedViewTitle>
            )}
          </EllipsisTooltip>
          <PinButton
            icon="pin02Filled"
            buttonType="tertiary"
            loading={isLoading}
            className="cardHoverDisable"
            aria-label="Unpin search"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              mutate({ input: { savedViewId: id } });
            }}
          />
        </Flex>
        {description && (
          <EllipsisTooltip fullText={description} tooltipProps={{ side: "bottom" }}>
            {(ref) => (
              <Text ref={ref} description ellipsis ellipsisMethod="lineClamp" ellipsisLines={2}>
                {description}
              </Text>
            )}
          </EllipsisTooltip>
        )}
      </Flex>
    </PinnedViewCard>
  );
};

const PinButton = styled(Button)({
  display: "none",
  opacity: 0, // For exit animation
  transition: "display 0.2s allow-discrete, opacity 0.2s, background-color 0.2s, border-color 0.2s",
});

const PinnedViewCard = styled(Card)({
  padding: "10px 16px",
  minHeight: "98px",
  "&:hover, &:focus-within": {
    // focus-within is needed for keyboard navigation
    [`${PinButton}`]: {
      display: "flex",
      opacity: 1,
      "@starting-style": {
        // Needed since animations don't run when changing from display: none by default
        opacity: 0,
      },
    },
  },
});

const PinnedViewTitle = styled(Text)({
  flexGrow: 1,
});

export default EmptyStatePinnedView;
